/**
 * The wishlist. Two halves, one write path.
 *
 * 🔴 SAVE FIRST, ASK SECOND. Tapping the heart saves — signed in or not, online or not.
 * The invitation to sign in comes afterwards, on the wishlist page, once there is
 * something worth keeping.
 *
 * ⚠️ LOCAL STORAGE IS THE WRITE-AHEAD LOG, NOT A SEPARATE FEATURE. Every save lands here
 * first and is pushed to the account afterwards. That is what makes the awkward cases
 * fall out for free instead of each needing its own branch: saving with no signal, saving
 * before signing in, saving while the proxy is down, and signing in on a browser that
 * already holds five pieces are all THE SAME PATH — write local, flush when there is
 * somewhere to flush to.
 *
 * The tempting alternative — signed-in customers write straight to the server, signed-out
 * ones write locally — is two stores with two failure modes, and the interesting bugs all
 * live in the seam between them.
 *
 * ⚠️ `account` IS PROVED BY THE ROUTE, NEVER ASSUMED FROM LIQUID. `data-djh-account` on
 * the body says only "worth asking". If the fetch fails or answers `signedIn:false` this
 * stays in device mode and the local log keeps everything — nothing is lost by guessing
 * wrong in that direction, whereas a save dropped because we ASSUMED an account was
 * reachable is not recoverable.
 *
 * ⚠️ VARIANT IDS ARE STRINGS. A Shopify variant id is a 17-digit bigint and PostgREST
 * hands it back as a string for that reason. `Number()` on one loses the low digits with
 * no error — it would save the wrong variant, silently, and only for large ids. Nothing
 * here coerces one.
 *
 * ⚠️ EVERY STORAGE CALL IS GUARDED — reads included. localStorage throws rather than
 * returning null in a private window, with site data blocked, and in some in-app
 * browsers. An unguarded read takes the script down and every heart on the page with it.
 */
(() => {
  // Re-entry guard. Two copies would register two click listeners on the same heart,
  // and a save that immediately un-saves looks exactly like a control that does nothing.
  if (window.djhWishlist) return;

  const KEY = 'djh.wishlist.v1';
  const ENDPOINT = '/apps/djh/wishlist';

  // ---------------------------------------------------------------- local log

  const readLocal = () => {
    try {
      const raw = window.localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_err) {
      return [];
    }
  };

  const writeLocal = (items) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
      return true;
    } catch (_err) {
      return false;
    }
  };

  // ------------------------------------------------------------- remote state

  let remote = [];
  // 🔴 TWO DIFFERENT QUESTIONS, AND THEY HAD ONE ANSWER.
  //
  //   identity — is this person signed in?      Liquid knows: `{% if customer %}` writes
  //                                             data-djh-account on <body>.
  //   storage  — can we read/write their list?  only the proxy knows.
  //
  // They were both `account`, taken from the proxy's `signedIn`. Measured in Henry's own
  // browser, 2026-09-08, signed in on the storefront:
  //
  //     window.__st.cid            9562357629250     ← Shopify has authenticated him
  //     <body data-djh-account>    "1"               ← Liquid agrees
  //     /apps/djh/wishlist         {signedIn:false}  ← the route does not
  //
  // So the page told a signed-in customer "Saved on this device" and offered him a link to
  // sign in. The IDENTITY was never in doubt; only the storage was.
  //
  // ⚠️ AND THE COPY MUST NOT OVERCORRECT. "Saved to your account" is a promise about
  // storage, so it still waits for the proxy. Signed in with a route that cannot see them
  // gets the true sentence — the list is on this device — and no sign-in ask, because
  // asking a signed-in person to sign in is the part that reads as broken.
  const signedInPerTheme = document.body.dataset.djhAccount === '1';
  let account = false;
  let flushing = false;

  let settle;
  const ready = new Promise((resolve) => { settle = resolve; });

  const asVariant = (v) => (v === null || v === undefined || v === '' ? null : String(v));

  /**
   * ⚠️ THE TWO HALVES TIMESTAMP DIFFERENTLY AND JS WILL NOT COMPARE THEM.
   *
   *   local   Date.now()                          1788735233111   number
   *   remote  savedAt "2026-09-06T22:53:11.638Z"  ISO 8601        string
   *
   * `"2026-09-06T…" > 1788735233111` is FALSE, and so is the reverse — a string/number
   * comparison coerces to NaN and every ordering question answers "no". The saved list
   * sorts newest-first, so mixing them silently scrambled the order:
   *
   *   comparator gave   remote2 → local → remote
   *   newest-first is   local → remote → remote2
   *
   * That is not a rare state. It is EVERY moment between a save and its flush, and the
   * whole first render after somebody signs in with pieces already on the device.
   *
   * Found by feeding OPS's real response bodies into the tests instead of the ones I had
   * invented — mine used `savedAt: '1'`, which sorts like a number and hid it.
   */
  const asMoment = (v) => {
    if (typeof v === 'number') return v;
    const parsed = Date.parse(v);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Pipe-joined. A handle is lowercase alphanumeric and hyphens, a variant id is digits,
  // so neither can carry the separator. Absent and empty collapse to one key on purpose:
  // a row saved without a size IS the sizeless row.
  const rowKey = (handle, variant) => handle + '|' + (asVariant(variant) || '');

  /**
   * Remote union local, remote winning. The union is what gets painted, so a piece saved
   * moments ago shows as saved whether or not the push has landed — the customer should
   * never watch their own save arrive.
   */
  const all = () => {
    const rows = new Map();
    remote.forEach((i) => {
      rows.set(rowKey(i.handle, i.variantId), {
        handle: i.handle,
        variant: asVariant(i.variantId),
        at: asMoment(i.savedAt),
        via: i.savedVia || 'storefront',
        synced: true,
      });
    });
    readLocal().forEach((i) => {
      const k = rowKey(i.handle, i.variant);
      if (rows.has(k)) return;
      rows.set(k, { handle: i.handle, variant: asVariant(i.variant), at: asMoment(i.at), via: 'storefront', synced: false });
    });
    return Array.from(rows.values());
  };

  // The heart is about the PIECE, not the size — the only thing a card heart can mean,
  // since a card has no variant to offer.
  const savedHandle = (handle) => all().some((i) => i.handle === handle);

  // ------------------------------------------------------------------ network

  const post = async (body) => {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('wishlist ' + res.status);
    return res.json();
  };

  const load = async () => {
    const res = await fetch(ENDPOINT, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('wishlist ' + res.status);
    const data = await res.json();
    // signedIn:false with an empty list is a 200 and a real state — signed out and
    // signed-in-with-nothing-saved are different, and the page says different things.
    account = data.signedIn === true;
    remote = account && Array.isArray(data.items) ? data.items : [];
    return data;
  };

  /**
   * Push the local log into the account.
   *
   * 🔴 CLEARS ON `merged`, NEVER ON THE STATUS CODE. A 200 that stored nothing is exactly
   * the case where clearing loses a customer's saves for good, and it is indistinguishable
   * from success at the transport layer.
   *
   * ⚠️ AND CLEARS ONLY WHAT IT SENT. A heart pressed while the request is in flight is in
   * the log but not in the merge; clearing the whole key would throw it away silently.
   */
  const flush = async () => {
    if (!account || flushing) return;
    const pending = readLocal();
    if (!pending.length) return;
    flushing = true;
    try {
      const result = await post({
        action: 'merge',
        items: pending.map((i) => {
          const item = { handle: i.handle };
          const v = asVariant(i.variant);
          if (v !== null) item.variantId = v;
          return item;
        }),
      });
      if (result && result.merged === true) {
        const sent = new Set(pending.map((i) => rowKey(i.handle, i.variant)));
        writeLocal(readLocal().filter((i) => !sent.has(rowKey(i.handle, i.variant))));
        await load();
        broadcast();
      }
    } catch (_err) {
      // Stays in the log and goes again on the next page. A failed sync must never look
      // like a failed save.
    } finally {
      flushing = false;
    }
  };

  // ----------------------------------------------------------------- painting

  const paint = (button, saved) => {
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.classList.toggle('is-saved', saved);
    // Two label sets ship in the markup and the mode picks one, rather than Liquid
    // choosing: this button sits inside a page Shopify may serve from cache, and a cached
    // "on this device" on a signed-in customer's screen is a promise we are breaking.
    const key = account
      ? (saved ? 'labelSavedAccount' : 'labelSaveAccount')
      : (saved ? 'labelSaved' : 'labelSave');
    const label = button.dataset[key] || (saved ? button.dataset.labelSaved : button.dataset.labelSave);
    if (label) button.setAttribute('aria-label', label);
    const text = button.querySelector('[data-djh-save-text]');
    if (text && button.dataset.textSaved && button.dataset.textSave) {
      text.textContent = saved ? button.dataset.textSaved : button.dataset.textSave;
    }
  };

  const repaint = () => {
    const items = all();
    document.querySelectorAll('[data-djh-save]').forEach((b) => {
      paint(b, items.some((i) => i.handle === b.dataset.handle));
    });

    // The product page's "See your saved pieces", shown once that piece is on the list.
    document.querySelectorAll('[data-djh-saved-link]').forEach((a) => {
      a.hidden = !items.some((i) => i.handle === a.dataset.handle);
    });

    // The header count. Counts PIECES, not rows: a ring saved in two sizes is one
    // thing the customer is thinking about, and "2" beside a heart when they saved
    // one ring reads as a bug in our counting rather than as two sizes.
    const pieces = new Set(items.map((i) => i.handle)).size;
    document.querySelectorAll('[data-djh-wishlist-count]').forEach((el) => {
      el.textContent = pieces;
      // Zero is not shown at all. A badge reading 0 is noise on every page a
      // customer has not saved anything on, which is most of them.
      el.hidden = pieces === 0;
    });
  };

  const broadcast = () => {
    repaint();
    document.dispatchEvent(new CustomEvent('djh:wishlist:change', { detail: { count: all().length, account } }));
  };

  const refuse = (button) => {
    // Storage refused and there is no account to fall back on. Saying so beats a heart
    // that fills and empties on reload: the customer would believe the piece was kept.
    button.classList.add('is-refused');
    window.setTimeout(() => button.classList.remove('is-refused'), 2000);
  };

  // ------------------------------------------------------------------ removal

  /**
   * A removal that deleted nothing did not happen, whatever the status code said.
   *
   * ⚠️ AN ABSENT COUNT IS UNKNOWN, NOT SUCCESS — and it is left alone rather than rolled
   * back. Rolling back on a missing field would resurrect rows the server really did
   * delete; treating it as success is the bug this exists to catch. Neither is safe to
   * assume, so the next load reconciles it and nothing is invented here.
   */
  const restoreIfNothingRemoved = (gone) => (result) => {
    if (result && typeof result.removed === 'number' && result.removed === 0) {
      remote = remote.concat(gone);
      broadcast();
    }
  };

  /**
   * Remove one exact row.
   *
   * 🔴 THE SCOPE IS NAMED, NEVER INFERRED FROM PRESENCE. `variantId: null` and an absent
   * `variantId` are the same JSON on the wire, and they meant opposite things — "the row
   * with no size" and "every row". So the caller says which it meant:
   *
   *     scope:'all'                          the heart — every row for the piece
   *     scope:'sizeless'                     the row saved without a size
   *     scope:'variant' + variantId:'…'      that one size
   *
   * ⚠️ `scope` IS ALWAYS A STRING AND THE VARIANT ID TRAVELS BESIDE IT. Nesting it as
   * `scope: { variantId }` reads naturally and is a 400 — the route stringifies scope, so
   * an object arrives as "[object Object]". OPS declined to accept both spellings, and was
   * right to: two spellings of one argument is what put the add-on pricing rule in two
   * places, and the wire is where the second one is hardest to notice.
   *
   * ⚠️ AND THE ANSWER IS A COUNT — `removed`, the number of rows the delete actually
   * matched, selected back rather than assumed. The route used to return `ok: !error`,
   * which said success when it deleted nothing — a Remove that removed nothing was
   * indistinguishable from one that worked. Zero rows removed is a failed removal here and
   * the row goes back, because leaving it gone on screen while it survives on the server
   * is a list that disagrees with itself on the next load.
   */
  const removeRow = (handle, variant) => {
    const v = asVariant(variant);
    const k = rowKey(handle, v);
    writeLocal(readLocal().filter((i) => rowKey(i.handle, i.variant) !== k));
    if (account) {
      const gone = remote.filter((i) => rowKey(i.handle, i.variantId) === k);
      if (gone.length) {
        remote = remote.filter((i) => rowKey(i.handle, i.variantId) !== k);
        const body = { action: 'remove', handle, scope: v === null ? 'sizeless' : 'variant' };
        if (v !== null) body.variantId = v;
        post(body)
          .then(restoreIfNothingRemoved(gone))
          .catch(() => { remote = remote.concat(gone); broadcast(); });
      }
    }
    broadcast();
  };

  const removePiece = (handle) => {
    writeLocal(readLocal().filter((i) => i.handle !== handle));
    if (account) {
      const gone = remote.filter((i) => i.handle === handle);
      if (gone.length) {
        remote = remote.filter((i) => i.handle !== handle);
        post({ action: 'remove', handle, scope: 'all' })
          .then(restoreIfNothingRemoved(gone))
          .catch(() => { remote = remote.concat(gone); broadcast(); });
      }
    }
    broadcast();
  };

  const addPiece = (handle, variant, button) => {
    const v = asVariant(variant);
    const stored = writeLocal(readLocal().concat([{ handle, variant: v, at: Date.now() }]));

    if (stored) {
      broadcast();
      flush();
      return;
    }

    if (!account) {
      refuse(button);
      return;
    }

    // No log available — a private window with an account signed in. Straight to the
    // server, which is the only store this browser has.
    const optimistic = { handle, variantId: v, savedAt: new Date().toISOString(), savedVia: 'storefront' };
    remote = remote.concat([optimistic]);
    broadcast();
    const body = { action: 'add', handle };
    if (v !== null) body.variantId = v;
    post(body).catch(() => {
      remote = remote.filter((i) => i !== optimistic);
      broadcast();
      refuse(button);
    });
  };

  // --------------------------------------------------------------------- wire

  /**
   * 🔴 THE HEART SAVED THE WRONG SIZE, AND IT SAVED IT CONFIDENTLY.
   *
   * `data-variant` is written by Liquid at RENDER time and never changes. Measured on
   * kanani-14k-8mm-ring, 2026-09-08:
   *
   *     choose size 7   →  the form carries 55315358581058
   *     press the heart →  55315358515522 was saved        (size 5, the render default)
   *
   * A wishlist that returns a size the customer did not choose is worse than one that
   * returns no size: they saved "the one I want" and got a different ring back, with
   * nothing on screen to tell them.
   *
   * ⚠️ SAME SELECTOR AS THE HINT DIALOG, DELIBERATELY. `.djh-product__form [name="id"]`
   * is the one place the PDP keeps the chosen variant across all three of its option
   * shapes — a <select name="id">, a hidden [data-djh-variant-id], and a bare hidden
   * input. djh-hint-form.js records why reading only one of those was wrong. Two readers,
   * one selector; a third answer to "which one did they choose" is how they disagree.
   *
   * ⚠️ AN EMPTY VALUE MEANS NOTHING IS CHOSEN AND IS SAVED AS NO VARIANT. That is the
   * explicit-choice state on a product that requires a size, and the honest record is the
   * PIECE — the wishlist card then says "Choose options" rather than offering to bag a
   * size nobody picked.
   *
   * Falls back to `data-variant` off the PDP entirely — a product card's heart in a
   * collection grid has no form to read, and there the render-time variant is the only
   * variant.
   */
  const chosenVariant = (button) => {
    const live = document.querySelector('.djh-product__form [name="id"]');
    if (live) return live.value ? String(live.value) : null;
    return button.dataset.variant || null;
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-djh-save]');
    if (!button) return;
    // Cards wrap their whole body in a link; without this the heart navigates instead of
    // saving, which is a control that appears to do nothing.
    event.preventDefault();
    event.stopPropagation();

    const handle = button.dataset.handle;
    if (savedHandle(handle)) removePiece(handle);
    else addPiece(handle, chosenVariant(button), button);
  });

  // Another tab may have saved or cleared something. A wishlist that disagrees with
  // itself across two open tabs is the small wrongness that makes a feature feel broken.
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) broadcast();
  });

  repaint();

  if (document.body.dataset.djhAccount === '1') {
    load()
      .then(() => { broadcast(); return flush(); })
      .catch(() => { /* device mode, log intact */ })
      .finally(() => settle());
  } else {
    settle();
  }

  window.djhWishlist = {
    read: all,
    ready,
    isAccount: () => account,
    // Identity, not storage. The list page uses this to decide what to SAY.
    isSignedIn: () => signedInPerTheme,
    removeRow,
    removePiece,
    key: KEY,
  };

  // Announced, so a consumer that loaded first can wait rather than give up. The list
  // page did exactly that for an hour and rendered blank: it read window.djhWishlist,
  // found nothing, and returned without a sound.
  document.dispatchEvent(new CustomEvent('djh:wishlist:ready'));
})();
